"use client";

import { useEffect, type RefObject } from "react";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function useForcedDialogFocus<T extends HTMLElement>(dialogRef: RefObject<T | null>) {
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const getFocusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      .filter((element) => element.getAttribute("aria-hidden") !== "true");
    const focusFirst = () => {
      (getFocusable()[0] ?? dialog).focus({ preventScroll: true });
    };
    const inertState = new Map<HTMLElement, { inert: boolean; ariaHidden: string | null }>();
    const makeInert = (element: HTMLElement) => {
      if (element.contains(dialog) || inertState.has(element)) return;
      inertState.set(element, {
        inert: element.inert,
        ariaHidden: element.getAttribute("aria-hidden"),
      });
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
    };
    for (const element of Array.from(document.body.children)) {
      if (element instanceof HTMLElement) makeInert(element);
    }
    const dashboardShell = document.querySelector<HTMLElement>("[data-protected-dashboard-shell]");
    if (dashboardShell) makeInert(dashboardShell);

    const dialogBodyRoot = Array.from(document.body.children).find((element) => element.contains(dialog));
    const restoreDialogAriaHidden = dialogBodyRoot instanceof HTMLElement
      ? dialogBodyRoot.getAttribute("aria-hidden")
      : null;
    if (dialogBodyRoot instanceof HTMLElement) dialogBodyRoot.removeAttribute("aria-hidden");

    // Some portal-based dialogs mount after this gate and try to become the
    // active focus scope. Isolate late body siblings as well, and keep this
    // higher-priority forced gate exposed to assistive technology.
    const bodyObserver = new MutationObserver((mutations) => {
      let isolatedLatePortal = false;
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node instanceof HTMLElement && !node.contains(dialog)) {
            makeInert(node);
            isolatedLatePortal = true;
          }
        }
      }
      if (isolatedLatePortal) focusFirst();
    });
    bodyObserver.observe(document.body, { childList: true });
    const dialogObserver = dialogBodyRoot instanceof HTMLElement
      ? new MutationObserver(() => dialogBodyRoot.removeAttribute("aria-hidden"))
      : null;
    dialogObserver?.observe(dialogBodyRoot as HTMLElement, {
      attributes: true,
      attributeFilter: ["aria-hidden"],
    });
    const animationFrame = window.requestAnimationFrame(focusFirst);
    const focusRetries = [
      window.setTimeout(focusFirst, 50),
      window.setTimeout(focusFirst, 250),
    ];
    const containFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !dialog.contains(event.target)) focusFirst();
    };
    const containKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener("focusin", containFocus, true);
    document.addEventListener("keydown", containKeyboard, true);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      focusRetries.forEach((timer) => window.clearTimeout(timer));
      bodyObserver.disconnect();
      dialogObserver?.disconnect();
      document.removeEventListener("focusin", containFocus, true);
      document.removeEventListener("keydown", containKeyboard, true);
      inertState.forEach((state, element) => {
        element.inert = state.inert;
        if (state.ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", state.ariaHidden);
      });
      if (dialogBodyRoot instanceof HTMLElement && restoreDialogAriaHidden !== null) {
        dialogBodyRoot.setAttribute("aria-hidden", restoreDialogAriaHidden);
      }
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
    };
  }, [dialogRef]);
}
